import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { OnboardingStep as StepType, OnboardingProgress } from '../hooks/useOnboarding'

interface OnboardingStepProps {
  step: StepType
  progress: OnboardingProgress
  isActive: boolean
  isCompleted: boolean
  onComplete: (skip?: boolean) => Promise<void>
  onExecuteAction: (action: string, params: Record<string, unknown>) => Promise<unknown>
}

export default function OnboardingStep({
  step,
  progress,
  isActive,
  isCompleted,
  onComplete,
  onExecuteAction,
}: OnboardingStepProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState<Record<string, string>>({})

  const handleManualComplete = async () => {
    setLoading(true)
    setError('')
    try {
      await onComplete(false)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  const handleSkip = async () => {
    setLoading(true)
    setError('')
    try {
      await onComplete(true)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  const handleFiscalPeriodCreate = async () => {
    setLoading(true)
    setError('')
    try {
      const year = new Date().getFullYear()
      const start = formData.start_date || `${year}-01-01`
      const end = formData.end_date || `${year}-12-31`
      await onExecuteAction('create_fiscal_period', { start_date: start, end_date: end })
      await onComplete(false)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  const handleChartOfAccountsSet = async (template: 'basic' | 'extended') => {
    setLoading(true)
    setError('')
    try {
      await onExecuteAction('set_chart_of_accounts', { template })
      await onComplete(false)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  const handleCreateSampleTransaction = async () => {
    setLoading(true)
    setError('')
    try {
      const amount = parseFloat(formData.amount || '100')
      const description = formData.description || 'Jäsenmaksu 2024'
      await onExecuteAction('create_sample_transaction', {
        description,
        amount,
        account_code: amount >= 0 ? '3000' : '4000',
      })
      await onComplete(false)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  if (!isActive && !isCompleted) {
    return (
      <div className="p-4 rounded-lg bg-gray-50 border border-gray-200 opacity-60">
        <h3 className="font-medium text-gray-500">{step.title}</h3>
        <p className="text-sm text-gray-400 mt-1">{step.description}</p>
      </div>
    )
  }

  if (isCompleted) {
    return (
      <div className="p-4 rounded-lg bg-green-50 border border-green-200">
        <div className="flex items-center gap-2">
          <span className="text-green-600 text-xl">✓</span>
          <h3 className="font-medium text-green-800">{step.title}</h3>
          {progress.skipped_steps?.includes(step.key) && (
            <span className="text-xs text-gray-500 ml-2">(ohitettu)</span>
          )}
        </div>
        <p className="text-sm text-green-600 mt-1">{step.description}</p>
      </div>
    )
  }

  return (
    <div className="p-5 rounded-lg bg-white border border-blue-200 shadow-sm">
      <h3 className="font-semibold text-gray-900 text-lg mb-1">{step.title}</h3>
      <p className="text-sm text-gray-500 mb-4">{step.description}</p>

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Fiscal Period Form */}
      {step.key === 'fiscal_period' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Useimmat yhdistykset käyttävät kalenterivuotta:</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Alkaa</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                value={formData.start_date || `${new Date().getFullYear()}-01-01`}
                onChange={e => setFormData(f => ({ ...f, start_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Päättyy</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                value={formData.end_date || `${new Date().getFullYear()}-12-31`}
                onChange={e => setFormData(f => ({ ...f, end_date: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleFiscalPeriodCreate}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded font-medium hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {loading ? t('common.creating') : t('setup.createFiscalYear')}
            </button>
            <button
              onClick={handleSkip}
              disabled={loading}
              className="text-sm text-gray-500 hover:text-gray-700 px-3"
            >
              Ohita
            </button>
          </div>
        </div>
      )}

      {/* Chart of Accounts */}
      {step.key === 'chart_of_accounts' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Valitse sopiva tilikartta yhdistyksellesi:</p>
          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => handleChartOfAccountsSet('basic')}
              disabled={loading}
              className="text-left p-4 border-2 border-blue-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <div className="font-medium text-gray-900">Perusyhdistys ⭐ Suositeltu</div>
              <div className="text-sm text-gray-500 mt-1">12 tiliä – sopii useimmille yhdistyksille. Sisältää perus tulo-, meno- ja tasetilit.</div>
            </button>
            <button
              onClick={() => handleChartOfAccountsSet('extended')}
              disabled={loading}
              className="text-left p-4 border-2 border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors"
            >
              <div className="font-medium text-gray-900">Laajennettu</div>
              <div className="text-sm text-gray-500 mt-1">18 tiliä – isommille yhdistyksille, joilla on monipuolisempi talous.</div>
            </button>
          </div>
          <button
            onClick={handleSkip}
            disabled={loading}
            className="text-sm text-gray-500 hover:text-gray-700 mt-2"
          >
            Ohita tämä vaihe
          </button>
        </div>
      )}

      {/* First Transaction */}
      {step.key === 'first_transaction' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Tee harjoituskirjaus kokeillaksesi kirjanpitoa:</p>
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Kuvaus</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="esim. Jäsenmaksu 2024"
                value={formData.description || ''}
                onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Summa (€, positiivinen = tulo)</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="100"
                value={formData.amount || ''}
                onChange={e => setFormData(f => ({ ...f, amount: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleCreateSampleTransaction}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded font-medium hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {loading ? t('common.creating') : t('setup.createEntry')}
            </button>
            <button
              onClick={handleManualComplete}
              disabled={loading}
              className="text-sm text-gray-500 hover:text-gray-700 px-3"
            >
              Merkitse valmiiksi
            </button>
            <button
              onClick={handleSkip}
              disabled={loading}
              className="text-sm text-gray-500 hover:text-gray-700 px-3"
            >
              Ohita
            </button>
          </div>
        </div>
      )}

      {/* Completed step */}
      {step.key === 'completed' && (
        <div className="text-center py-4">
          <div className="text-4xl mb-2">🎉</div>
          <p className="text-gray-600">Onboarding on suoritettu! Voit nyt tutustua dashboardiin.</p>
          <button
            onClick={() => onComplete(false)}
            className="mt-4 bg-green-600 text-white py-2 px-6 rounded font-medium hover:bg-green-700"
          >
            Siirry dashboardille
          </button>
        </div>
      )}
    </div>
  )
}
